// Wire message encoding/decoding and plaintext framing — pure functions

import { type Result, ok, err } from './types.js';
import { TAG_SIZE, NONCE_SIZE, SIG_SIZE, MSG_SIZE } from './constants.js';
import { randomBytes } from './crypto.js';

/** Wire format: [senderTag:32][roomTag:32][nonce:16][ciphertext:N] */
export type WireMessage = Readonly<{
  readonly senderTag: Uint8Array;
  readonly roomTag: Uint8Array;
  readonly nonce: Uint8Array;
  readonly ciphertext: Uint8Array;
}>;

const WIRE_HEADER_SIZE = TAG_SIZE + TAG_SIZE + NONCE_SIZE; // 32 + 32 + 16 = 80

/** Encode a WireMessage into a single Uint8Array. */
export const encodeWireMessage = (msg: WireMessage): Uint8Array => {
  const result = new Uint8Array(WIRE_HEADER_SIZE + msg.ciphertext.length);
  result.set(msg.senderTag, 0);
  result.set(msg.roomTag, TAG_SIZE);
  result.set(msg.nonce, TAG_SIZE + TAG_SIZE);
  result.set(msg.ciphertext, WIRE_HEADER_SIZE);
  return result;
};

/** Decode a Uint8Array into a WireMessage. */
export const decodeWireMessage = (data: Uint8Array): Result<WireMessage, string> => {
  if (data.length < WIRE_HEADER_SIZE + 1) {
    return err(`Wire message too short: ${data.length} bytes (minimum ${WIRE_HEADER_SIZE + 1})`);
  }
  return ok({
    senderTag: data.slice(0, TAG_SIZE),
    roomTag: data.slice(TAG_SIZE, TAG_SIZE + TAG_SIZE),
    nonce: data.slice(TAG_SIZE + TAG_SIZE, WIRE_HEADER_SIZE),
    ciphertext: data.slice(WIRE_HEADER_SIZE),
  });
};

/**
 * Build a padded plaintext: [length:2][message:N][signature:64][randomPadding] = MSG_SIZE total.
 * Length is stored as big-endian uint16.
 */
export const buildPlaintext = (message: Uint8Array, signature: Uint8Array): Result<Uint8Array, string> => {
  if (signature.length !== SIG_SIZE) {
    return err(`Signature must be ${SIG_SIZE} bytes, got ${signature.length}`);
  }
  const overhead = 2 + message.length + SIG_SIZE; // length prefix + message + signature
  if (overhead > MSG_SIZE) {
    return err(`Message too large: ${message.length} bytes (max ${MSG_SIZE - 2 - SIG_SIZE})`);
  }
  const plaintext = new Uint8Array(MSG_SIZE);
  // 2-byte big-endian length prefix
  plaintext[0] = (message.length >> 8) & 0xff;
  plaintext[1] = message.length & 0xff;
  // Message content
  plaintext.set(message, 2);
  // Signature immediately after message
  plaintext.set(signature, 2 + message.length);
  // Random padding fills the rest
  const paddingStart = 2 + message.length + SIG_SIZE;
  const paddingLength = MSG_SIZE - paddingStart;
  if (paddingLength > 0) {
    const padding = randomBytes(paddingLength);
    plaintext.set(padding, paddingStart);
  }
  return ok(plaintext);
};

/**
 * Parse a padded plaintext back into message and signature.
 */
export const parsePlaintext = (plaintext: Uint8Array): Result<Readonly<{ message: Uint8Array; signature: Uint8Array }>, string> => {
  if (plaintext.length !== MSG_SIZE) {
    return err(`Plaintext must be ${MSG_SIZE} bytes, got ${plaintext.length}`);
  }
  const messageLength = (plaintext[0] << 8) | plaintext[1];
  if (2 + messageLength + SIG_SIZE > MSG_SIZE) {
    return err(`Invalid message length: ${messageLength}`);
  }
  const message = plaintext.slice(2, 2 + messageLength);
  const signature = plaintext.slice(2 + messageLength, 2 + messageLength + SIG_SIZE);
  return ok(Object.freeze({ message, signature }));
};
